/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import React from 'react'
import './ServiceTypeSection.scss'
import { CardBody, Card, CardHeader } from 'reactstrap'
import { Fragment } from 'react'
import { DeductiblesComponent } from '../Deductibles/Deductibles'
import { CoInsurance } from '../CoInsurance/CoInsurance'
import { LimitationsComponent } from '../Limitations/Limitations'
import { DescriptionsComponent } from '../Descriptions/Descriptions'
import { InsuranceSubHeader } from '../InsuranceSubHeader/InsuranceSubHeader'
import { PeriodEndDate } from '../PeriodEndDate/PeriodEndDate'
import { LifetimeReserveComponent } from '../LifetimeReserve/LifetimeReserve'
import { Copays } from '../Copays/Copays'
import { CommonUtils } from '../../../../utils/CommonUtils'
import { ServiceTypeHelper } from '../../../../utils/ServiceTypeHelper'
import { IBaseSection, IInpatientTypeInsurance, ICoverageDiscoveryResponseStateManager } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { AcupunctureLimitations } from '../AcupunctureLimitations/AcupunctureLimitations'

export interface IServiceTypeSectionProps {
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}

export class ServiceTypeSection extends React.Component<IServiceTypeSectionProps> {
	acupunctureServiceTypeCode = '64'
	containsSubDetails(model: IInpatientTypeInsurance): boolean {
		// Deductibles
		if (model.deductibles && ((!CommonUtils.isUndefinedOrNull(model.deductibles.episodes) && model.deductibles.episodes.length > 0) || (model.deductibles.schedules && model.deductibles.schedules.length > 0))) {
			return true
		}

		// Copays
		if (this.hasCopays(model)) {
			return true
		}

		// Life Time Reserve
		if (this.hasLifeTimeReserve(model)) {
			return true
		}

		// CoInsurances
		if (model.coInsurances && model.coInsurances.length > 0) {
			return true
		}

		// Descriptions
		if (model.descriptions && model.descriptions.length > 0) {
			return true
		}

		// Limitations
		if (model.limitations && model.limitations.length > 0) {
			return true
		}

		return false
	}
	hasLifeTimeReserve(model: IInpatientTypeInsurance): boolean {
		if (model.reserve && model.reserve.lifetime && model.reserve.lifetime > 0) return true
		else return false
	}
	hasCopays(model: IInpatientTypeInsurance): boolean {
		const episodesCount = !model.coPayments || !model.coPayments.episodes ? 0 : model.coPayments.episodes.length
		const schedulesCount = !model.coPayments || !model.coPayments.schedules ? 0 : model.coPayments.schedules.length

		return episodesCount > 0 || schedulesCount > 0
	}

	renderServiceTypedata = (val: IBaseSection): JSX.Element[] | false => {
		const SelectedSections = val.summary.selectedSections
		const serviceTypes = ServiceTypeHelper.getMedicareSTC(val)
		const serviceTypesOrdered = serviceTypes && serviceTypes.sort((item1, item2) => CommonUtils.compareProperty(item1.definition, item2.definition))
		return (
			serviceTypesOrdered &&
			serviceTypesOrdered.length > 0 &&
			ServiceTypeHelper.getMedicareSTC(val).map((res, index) => {
				let contentElements: JSX.Element[] = []
				const endOfPeriodInsurance = res.insurances.filter(i => i.endOfPeriod2)
				const insurances = res.insurances.filter(i => !i.endOfPeriod2 && this.containsSubDetails(i))
				const insurancesContent = insurances.map((ins, index) => {
					return (
						<Fragment key={index}>
							<InsuranceSubHeader data={ins} />
							{ins.deductibles && <DeductiblesComponent data={ins.deductibles} />}
							<Copays data={ins} />
							<LifetimeReserveComponent data={ins} />
							<CoInsurance data={ins} />
							<DescriptionsComponent data={ins} />
							{res.code === this.acupunctureServiceTypeCode ? <AcupunctureLimitations data={ins} /> : <LimitationsComponent data={ins} />}
						</Fragment>
					)
				})

				if (insurancesContent && insurancesContent.length > 0) {
					contentElements = contentElements.concat(insurancesContent)
				}
				if (endOfPeriodInsurance && endOfPeriodInsurance.length > 0 && endOfPeriodInsurance[0]) {
					contentElements.push(<PeriodEndDate data={endOfPeriodInsurance[0]} />)
				}

				return (
					<Fragment key={index}>
						<tbody key={index + 'a'} className="summaryBody" style={{ display: res.code && SelectedSections.indexOf(res.code) !== -1 ? '' : 'none' }}>
							<tr className={`summarySection ${res.isActive ? '' : 'noCoverage'}`}>
								<td colSpan={2}>
									<h4>
										{res.definition}&nbsp;({res.code})
									</h4>
								</td>
								<td className={res.isActive ? 'active' : 'alert alert-error'}>
									<strong>{res.status}</strong>
								</td>
								<td className="summaryHide">
									<input type="hidden" value={res.code} />
								</td>
							</tr>
						</tbody>
						{contentElements.length > 0 ? (
							<tbody key={index + 'b'} className="summaryBody" style={{ display: res.code && SelectedSections.indexOf(res.code) !== -1 ? '' : 'none' }}>
								{contentElements}
							</tbody>
						) : null}
					</Fragment>
				)
			})
		)
	}

	render(): React.ReactNode {
		const section = this.props.CoverageDiscoveryResponseStateManager.selectedMedicareServiceTypeSection
		if (!this.props.CoverageDiscoveryResponseStateManager.selectedMedicareServiceTypeSection) return <></>
		else {
			return (
				<div className="accordion-group service-coverage">
					<div id={section.id} className="section" data-loadorder={section.loadOrder} data-index={section.summary.index}>
						<Card style={{ marginBottom: '1rem' }} className="card" key={section.displayOrder}>
							<CardHeader data-event={section.displayOrder} className="service-coverage accordion-heading whiteText">
								{section.summary.title}
							</CardHeader>
							<CardBody className="card-body">
								<div className="container" style={{ padding: 0, margin: 0, width: '100%' }}>
									<div className="cdResponsePage">
										<table className="service-types-table">
											<tbody>
												<tr className="summaryDisplaying">
													<td colSpan={4}>
														Displaying {section.summary.selectedSections.length} of {section.summary.sections.length} sections
													</td>
												</tr>
											</tbody>
											{this.renderServiceTypedata(section)}
										</table>
									</div>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			)
		}
	}
}
